import React from 'react'
import { useFieldArray, useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { HStack } from '~/components/elements/HStack'
import {
  StyledButton,
  type StyledButtonProps,
} from '~/components/elements/StyledButton'
import { Typography } from '~/components/elements/Typography'
import { VStack } from '~/components/elements/VStack'
import { useSurveyForSettingsQueryQuestions } from '~/hooks/useSurveyForSettingsQuery'

import { type InputValues } from '../utils'

import { ConditionInput } from './ConditionInput'

export type ConditionGroupInputProps = {
  conditionGroupIndex: number
  nineSegmentIndex: number
  onDelete: (conditionGroupIndex: number) => void
}

export const ConditionGroupInput = ({
  conditionGroupIndex,
  nineSegmentIndex,
  onDelete,
}: ConditionGroupInputProps) => {
  const { t } = useTranslation()
  const { control } = useFormContext<InputValues>()
  const { append, fields, remove } = useFieldArray({
    control,
    keyName: 'fieldId',
    name: `segment_definitions.${nineSegmentIndex}.condition_groups.${conditionGroupIndex}`,
  })

  const questions = useSurveyForSettingsQueryQuestions()
  const handleAddButtonClick = React.useCallback<
    EH<StyledButtonProps, 'onClick'>
  >(() => {
    append({
      question_id: questions[0].question_id,
      operation: 'equal',
      values: [],
      text_values: [],
      use_brand_number_as_answer: true,
      use_brand_number_as_question_number: true,
    })
  }, [append, questions])

  const handleDeleteButtonClick = React.useCallback<
    EH<StyledButtonProps, 'onClick'>
  >(() => {
    onDelete(conditionGroupIndex)
  }, [conditionGroupIndex, onDelete])

  return (
    <VStack border="normal" padding={4} spacing={4}>
      {fields.map((field, conditionIndex) => (
        <React.Fragment key={field.fieldId}>
          {conditionIndex !== 0 && (
            <Typography fontSize={16} variant="normal">
              And
            </Typography>
          )}

          <ConditionInput
            conditionGroupIndex={conditionGroupIndex}
            conditionIndex={conditionIndex}
            nineSegmentIndex={nineSegmentIndex}
            onDelete={remove}
          />
        </React.Fragment>
      ))}

      <HStack spacing={4}>
        <StyledButton onClick={handleAddButtonClick}>
          {t('add_definition')}
        </StyledButton>

        <StyledButton onClick={handleDeleteButtonClick}>
          {t('delete_definition_group')}
        </StyledButton>
      </HStack>
    </VStack>
  )
}
